import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Clock, CreditCard, Download, RefreshCw, Send } from "lucide-react";
import { toast } from "sonner";
import { api, czk, errMsg, openBlob, STATUS } from "../lib/api";
import { Daisy } from "../components/Daisy";
import { useSeo } from "../components/Seo";

const Confetti = () => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden">
    {Array.from({ length: 14 }).map((_, k) => (
      <motion.div key={k} className="absolute" style={{ left: `${(k * 7.3) % 100}%`, top: -40 }} animate={{ y: [0, 700], rotate: [0, 360 * (k % 2 ? 1 : -1)] }} transition={{ duration: 3 + (k % 4), delay: k * 0.15, ease: "easeIn" }}>
        <Daisy size={24 + (k % 3) * 10} />
      </motion.div>
    ))}
  </div>
);

export function PaymentSuccess() {
  useSeo({ title: "Platba" });
  const [sp] = useSearchParams();
  const sid = sp.get("session_id");
  const [state, setState] = useState("checking");
  const [orderId, setOrderId] = useState(null);
  useEffect(() => {
    let n = 0;
    let stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        setOrderId(data.order_id);
        if (data.payment_status === "paid") return setState("paid");
        if (data.status === "expired") return setState("failed");
      } catch {
        return setState("failed");
      }
      if (++n < 15) setTimeout(poll, 2000);
      else setState("timeout");
    };
    poll();
    return () => { stop = true; };
  }, [sid]);
  return (
    <div className="relative mx-auto max-w-xl px-4 py-24 text-center" data-testid="payment-result">
      {state === "paid" && <Confetti />}
      <span className="mx-auto grid h-32 w-32 place-items-center rounded-full bg-[#A3AC86]"><Daisy size={90} className={state === "checking" ? "animate-spin" : ""} /></span>
      {state === "checking" && <p className="h-section mt-8 text-3xl" data-testid="payment-checking">Ověřujeme platbu…</p>}
      {state === "paid" && (
        <>
          <h1 className="h-display mt-8 text-4xl" data-testid="payment-success">Děkujeme, zaplaceno!</h1>
          <p className="mt-3 text-[#6E7565]">Soubory jsou připraveny ve vašem účtu. Fakturu jsme vám poslali e-mailem.</p>
          <Link to="/ucet?tab=soubory" className="btn-sage mt-8" data-testid="payment-downloads-btn"><Download className="h-4 w-4" /> Stáhnout soubory</Link>
        </>
      )}
      {(state === "failed" || state === "timeout") && (
        <>
          <h1 className="h-section mt-8 text-3xl" data-testid="payment-failed">{state === "timeout" ? "Platba se stále zpracovává" : "Platba nebyla dokončena"}</h1>
          <p className="mt-3 text-[#6E7565]">Stav objednávky najdete ve svém účtu.</p>
          <Link to={orderId ? `/objednavka/${orderId}` : "/ucet"} className="btn-sage mt-8">Zobrazit objednávku</Link>
        </>
      )}
    </div>
  );
}

export function PaymentComgate() {
  useSeo({ title: "Platba" });
  const [sp] = useSearchParams();
  const id = sp.get("order") || sp.get("refId");
  const [state, setState] = useState("checking");
  useEffect(() => {
    let n = 0;
    let stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/payments/comgate/${id}`);
        if (data.status === "paid") return setState("paid");
        if (data.status === "cancelled") return setState("failed");
      } catch {
        return setState("failed");
      }
      if (++n < 15) setTimeout(poll, 2500);
      else setState("failed");
    };
    poll();
    return () => { stop = true; };
  }, [id]);
  return (
    <div className="relative mx-auto max-w-xl px-4 py-24 text-center" data-testid="comgate-result">
      {state === "paid" && <Confetti />}
      <span className="mx-auto grid h-32 w-32 place-items-center rounded-full bg-[#A3AC86]"><Daisy size={90} className={state === "checking" ? "animate-spin" : ""} /></span>
      <h1 className="h-display mt-8 text-4xl">{state === "checking" ? "Ověřujeme platbu…" : state === "paid" ? "Děkujeme, zaplaceno!" : "Platba zatím nebyla potvrzena"}</h1>
      {state !== "checking" && <Link to={state === "paid" ? "/ucet?tab=soubory" : `/objednavka/${id}`} className="btn-sage mt-8">{state === "paid" ? "Stáhnout soubory" : "Zobrazit objednávku"}</Link>}
    </div>
  );
}

export function PaymentCancel() {
  const [sp] = useSearchParams();
  return (
    <div className="mx-auto max-w-xl px-4 py-24 text-center" data-testid="payment-cancel">
      <Daisy size={90} className="mx-auto animate-wiggle" />
      <h1 className="h-section mt-6 text-3xl">Platba byla zrušena</h1>
      <p className="mt-3 text-[#6E7565]">Objednávka zůstává uložena – můžete ji zaplatit kartou nebo převodem.</p>
      <Link to={sp.get("order") ? `/objednavka/${sp.get("order")}` : "/ucet"} className="btn-sage mt-8">Dokončit platbu</Link>
    </div>
  );
}

export function OrderPage() {
  const { id } = useParams();
  useSeo({ title: "Objednávka" });
  const [o, setO] = useState(null);
  const [qr, setQr] = useState("");
  const load = () => api.get(`/orders/${id}`).then((r) => setO(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
    openBlob(api, `/orders/${id}/qr.png`).then(setQr).catch(() => {});
  }, [id]); // eslint-disable-line
  const payCard = async () => {
    try {
      const { data } = await api.post(`/orders/${id}/pay-stripe`);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const reportPaid = async () => {
    try {
      await api.post(`/orders/${id}/report-paid`);
      toast.success("Děkujeme! Jakmile platbu ověříme, pošleme fakturu a odemkneme soubory.");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!o) return <div className="grid min-h-[50vh] place-items-center"><Daisy size={60} className="animate-spin" /></div>;
  const st = STATUS[o.status];
  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-8" data-testid="order-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-soft overflow-hidden">
        <div className="bg-[#A3AC86] p-8 text-white">
          <CheckCircle2 className="h-10 w-10" />
          <h1 className="mt-3 font-serif text-3xl font-extrabold italic">Objednávka č. {o.number}</h1>
          <span className={`mt-3 inline-block rounded-full px-3 py-1 text-xs font-semibold ${st.cls}`} data-testid="order-status">{st.label}</span>
        </div>
        <div className="space-y-6 p-6 sm:p-8">
          {o.status === "pending" && (
            <div className="grid items-center gap-6 rounded-3xl bg-[#E9EFE0] p-6 sm:grid-cols-[180px_1fr]" data-testid="order-qr-box">
              {qr ? <img src={qr} alt="QR platba" className="mx-auto h-44 w-44 rounded-2xl bg-white p-2" data-testid="order-qr-image" /> : <div className="h-44 w-44 rounded-2xl bg-white" />}
              <div className="space-y-1 text-sm">
                <p className="h-section text-xl">Zaplaťte QR kódem</p>
                <p>Číslo účtu: <b>{o.payment.bank_account}</b></p>
                <p>IBAN: <b>{o.payment.iban}</b></p>
                <p>Variabilní symbol: <b data-testid="order-vs">{o.vs}</b></p>
                <p>Částka: <b>{czk(o.total)}</b></p>
                {o.customer_reported_at ? (
                  <p className="mt-2 flex items-center gap-2 rounded-2xl bg-white p-3 text-[#7A8460]" data-testid="order-reported-note"><Clock className="h-4 w-4" /> Platbu jste nahlásil(a). Čeká na potvrzení obchodníkem – poté vám přijde faktura a odemknou se soubory.</p>
                ) : (
                  <button className="btn-yellow mt-2" onClick={reportPaid} data-testid="order-report-paid-btn"><Send className="h-4 w-4" /> Zaplatil(a) jsem převodem</button>
                )}
                <div className="flex flex-wrap gap-2 pt-3">
                  {o.methods?.stripe && <button className="btn-sage" onClick={payCard} data-testid="order-pay-card-btn"><CreditCard className="h-4 w-4" /> Zaplatit kartou</button>}
                  <button className="btn-ghost" onClick={load} data-testid="order-refresh-btn"><RefreshCw className="h-4 w-4" /> Obnovit stav</button>
                </div>
              </div>
            </div>
          )}
          {o.status === "paid" && <Link to="/ucet?tab=soubory" className="btn-sage" data-testid="order-downloads-btn"><Download className="h-4 w-4" /> Přejít ke stažení</Link>}
          <ul className="divide-y text-sm">
            {o.items.map((i) => <li key={i.product_id} className="flex justify-between py-2"><span>{i.name} × {i.qty}</span><span>{czk(i.price * i.qty)}</span></li>)}
            {o.discount > 0 && <li className="flex justify-between py-2 text-[#D96B43]"><span>Sleva</span><span>-{czk(o.discount)}</span></li>}
            {o.voucher_amount > 0 && <li className="flex justify-between py-2 text-[#D96B43]" data-testid="order-voucher-line"><span>Dárkový poukaz {o.voucher}</span><span>-{czk(o.voucher_amount)}</span></li>}
            <li className="flex justify-between py-3 text-base font-bold"><span>Celkem</span><span>{czk(o.total)}</span></li>
          </ul>
        </div>
      </motion.div>
    </div>
  );
}
